import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LogIn,
  Calculator,
  TrendingUp,
  Mail,
  HeadphonesIcon,
  Shield,
  Clock,
  CreditCard,
  Smartphone,
  Globe,
  Lock,
  CheckCircle,
  ArrowRight,
  Star,
  Users,
  Award
} from 'lucide-react';

// Import the professional images
import businessTeamImage from '../assets/smiling-business-team-using-laptop.jpg';
import businessMeetingImage from '../assets/three-serious-partners-watching-presentation-digital-tablet.jpg';
import consultationImage from '../assets/executives-giving-ideas-meeting.jpg';
import customerServiceImage from '../assets/portrait-happy-young-colleagues-using-digital-tablet.jpg';

const Services = () => {
  const heroServices = [
    {
      icon: LogIn,
      title: "Online Banking",
      description: "24/7 secure access to your accounts from anywhere",
      features: ["Mobile Banking", "Account Management", "Transaction History"],
      link: "/login",
      bgColor: "from-blue-600 to-blue-700"
    },
    {
      icon: Calculator,
      title: "Bill Pay",
      description: "Pay all your bills online securely and conveniently",
      features: ["Automatic Payments", "Bill Scheduling", "Payment History"],
      link: "/bill-pay",
      bgColor: "from-green-600 to-green-700"
    },
    {
      icon: TrendingUp,
      title: "Wire Transfers",
      description: "Fast domestic and international money transfers",
      features: ["Same-day Processing", "Global Network", "Secure Transfers"],
      link: "/wire-transfer",
      bgColor: "from-purple-600 to-purple-700"
    },
    {
      icon: Mail,
      title: "Zelle Payments",
      description: "Send money instantly to friends and family",
      features: ["Instant Transfers", "No Fees", "Secure Payments"],
      link: "/zelle",
      bgColor: "from-orange-600 to-orange-700"
    }
  ];

  const additionalServices = [
    {
      icon: CreditCard,
      title: "Card Services",
      description: "Comprehensive credit and debit card solutions",
      features: ["Rewards Programs", "Fraud Protection", "Mobile Payments", "Contactless Technology"],
      link: "/cards"
    },
    {
      icon: HeadphonesIcon,
      title: "Customer Support",
      description: "Expert assistance when you need it most",
      features: ["24/7 Email Support", "Live Chat", "Online Help Center", "In-Branch Assistance"],
      link: "/support"
    },
    {
      icon: Shield,
      title: "Security Services",
      description: "Advanced protection for your financial data",
      features: ["Multi-Factor Authentication", "Fraud Monitoring", "Secure Encryption", "Identity Protection"],
      link: "/about"
    },
    {
      icon: Smartphone,
      title: "Mobile Banking",
      description: "Full-featured banking on your mobile device",
      features: ["Mobile Check Deposit", "Account Alerts", "ATM Locator", "Touch ID/Face ID"],
      link: "/login"
    },
    {
      icon: Globe,
      title: "International Services",
      description: "Global banking solutions for your needs",
      features: ["Foreign Exchange", "International Wires", "Travel Cards", "Multi-Currency Accounts"],
      link: "/wire-transfer"
    },
    {
      icon: Users,
      title: "Business Services",
      description: "Comprehensive solutions for businesses",
      features: ["Business Banking", "Merchant Services", "Payroll Processing", "Cash Management"],
      link: "/business-banking"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Small Business Owner",
      content: "The online banking platform is incredibly user-friendly. I can manage my business accounts efficiently from anywhere.",
      rating: 5
    },
    {
      name: "Michael Chen",
      role: "Financial Advisor",
      content: "Their wire transfer service is fast and reliable. My clients appreciate the quick international transfers.",
      rating: 5
    },
    {
      name: "Emily Rodriguez",
      role: "Working Professional",
      content: "Zelle payments make splitting bills and sending money to family so convenient. The customer service is exceptional.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative py-20 lg:py-32 overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900"
        style={{
          backgroundImage: `linear-gradient(rgba(14, 28, 64, 0.8), rgba(14, 28, 64, 0.8)), url(${businessTeamImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/90 to-primary-800/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Banking Services
                <span className="block text-3xl md:text-5xl bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
                  Made Simple
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto mb-8">
                Experience modern banking with our comprehensive suite of digital services designed for your convenience and security.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/login"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-primary-900 bg-white rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <LogIn className="w-6 h-6 mr-2" />
                Access Online Banking
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white rounded-xl hover:bg-white hover:text-primary-900 transition-all duration-300"
              >
                <HeadphonesIcon className="w-6 h-6 mr-2" />
                Get Support
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Core Banking Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need for modern banking, accessible 24/7 from any device
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {heroServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <Link to={service.link} className="block">
                  <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full">
                    <div className={`w-16 h-16 bg-gradient-to-r ${service.bgColor} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <service.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <ul className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-500">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center text-primary-600 font-semibold group-hover:text-primary-700">
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Additional Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive banking solutions tailored to meet all your financial needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <Link to={service.link} className="block">
                  <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl hover:border-primary-200 transition-all duration-300 transform hover:-translate-y-1 h-full">
                    <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary-100 transition-colors duration-300">
                      <service.icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <ul className="space-y-1 mb-4">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-500">
                          <CheckCircle className="w-3 h-3 text-green-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center text-primary-600 font-medium group-hover:text-primary-700">
                      Explore
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Highlights */}
      <section 
        className="py-20 relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(59, 130, 246, 0.9), rgba(29, 78, 216, 0.9)), url(${businessMeetingImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Why Choose Our Services?
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Experience banking that adapts to your lifestyle with cutting-edge technology and personalized service.
              </p>
              
              <div className="space-y-6">
                {[
                  {
                    icon: Lock,
                    title: "Bank-Level Security",
                    description: "Your data is protected with the highest security standards"
                  },
                  {
                    icon: Clock,
                    title: "24/7 Availability",
                    description: "Access your accounts and services anytime, anywhere"
                  },
                  {
                    icon: Award,
                    title: "Award-Winning Service",
                    description: "Recognized for excellence in customer service and innovation"
                  }
                ].map((highlight, index) => (
                  <motion.div
                    key={highlight.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start space-x-4"
                  >
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <highlight.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">{highlight.title}</h3>
                      <p className="text-blue-100">{highlight.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8"
            >
              <h3 className="text-2xl font-bold text-white mb-6">Start Banking Today</h3>
              <p className="text-blue-100 mb-6">
                Join thousands of satisfied customers who trust us with their financial needs.
              </p>
              <div className="space-y-4">
                <Link
                  to="/login"
                  className="w-full bg-white text-primary-600 py-4 px-6 rounded-xl font-semibold hover:bg-gray-50 transition-colors duration-300 flex items-center justify-center"
                >
                  <LogIn className="w-5 h-5 mr-2" />
                  Access Online Banking
                </Link>
                <Link
                  to="/contact"
                  className="w-full border-2 border-white text-white py-4 px-6 rounded-xl font-semibold hover:bg-white hover:text-primary-600 transition-all duration-300 flex items-center justify-center"
                >
                  <HeadphonesIcon className="w-5 h-5 mr-2" />
                  Schedule Consultation
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real experiences from real customers who trust our services
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 shadow-lg"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <blockquote className="text-gray-600 mb-4">
                  "{testimonial.content}"
                </blockquote>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(16, 185, 129, 0.9), rgba(5, 150, 105, 0.9)), url(${consultationImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Experience Better Banking?
            </h2>
            <p className="text-xl text-green-100 mb-8">
                              Join First Capital International and discover the difference our services can make for your financial future.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-green-600 bg-white rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <LogIn className="w-6 h-6 mr-2" />
                Open account
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white rounded-xl hover:bg-white hover:text-green-600 transition-all duration-300"
              >
                <HeadphonesIcon className="w-6 h-6 mr-2" />
                Speak with Expert
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services; 